import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LucideIcon, Loader2, XCircle, Eye } from 'lucide-react';
import { automationLogService } from '../services/automationLogService';
import { employeesService } from '../services/employeesService';
import GeminiStatusBanner from './GeminiStatusBanner';
import ThemeToggle from './ThemeToggle';
import AccountMenu from './AccountMenu';
import { AlertBell } from '../features/alerts/AlertBell';
import { CompanyMultiSelect } from './ui/CompanyMultiSelect';
import { IconBackButton } from './ui/BackButton';
import {
  useCompanyFilter,
  COMPANY_FILTER_ROUTES,
} from '../contexts/CompanyFilterContext';

interface HeaderProps {
  title: string;
  subtitle?: string;
  actionButton?: {
    text: string;
    icon: LucideIcon;
    onClick: () => void;
  };
  /** Texto pequeno acima do título (ex.: "Ocorrências" no Atendimento HUB). */
  eyebrow?: string;
  /** Mostra o voltar quadrado antes do título. */
  onBack?: { label: string; onClick: () => void };
}

const Header: React.FC<HeaderProps> = ({ title, subtitle, actionButton, eyebrow, onBack }) => {
  const { user, logout, hasCap, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    showSelector: showCompanySelector,
    availableCompanies,
    selectedCompanyIds,
    setSelectedCompanyIds,
  } = useCompanyFilter();
  // Só nas rotas que consomem o filtro — nas demais o seletor seria inerte.
  const companyFilterApplies = COMPANY_FILTER_ROUTES.some((route) =>
    route.test(location.pathname),
  );
  // Contadores de status - calculados da mesma forma que no Painel de Automações (apenas para Desenvolvedor)
  // react-query: refetch a cada 30s e pausa sozinho quando a aba está em background
  // (refetchIntervalInBackground é false por padrão no react-query v3).
  const { data: statusCounts = null } = useQuery(
    ['header-automation-status-counts'],
    async () => {
      // Buscar muitos logs (sem filtros) para ter contadores precisos — mesma lógica do Painel de Automações
      const response = await automationLogService.getExecutionHistory({
        limit: 50,
        offset: 0,
      });
      const logs = response.logs;
      return {
        running: logs.filter(log => log.status === 'RUNNING').length,
        finished: logs.filter(log => log.status === 'FINISHED').length,
        failed: logs.filter(log => log.status === 'FAILED').length,
      };
    },
    {
      enabled: !!user && hasCap('can_use_dev_tools'),
      refetchInterval: 30000,
    },
  );

  const handleExitViewAs = async () => {
    try {
      await employeesService.updateMe({ view_as_company_id: null });
      await refreshUser();
    } catch (error) {
      console.error('Erro ao sair do modo de visualização:', error);
    }
  };


  return (
    <>
      {user?.view_as_company_id != null && (
        <div className="w-full bg-amber-500 text-white text-sm px-6 py-1.5 flex items-center justify-center gap-3 flex-shrink-0">
          <Eye className="w-4 h-4" />
          <span>
            Visualizando como externo:{' '}
            <strong>
              {user.view_as_company_name ?? `empresa #${user.view_as_company_id}`}
            </strong>
          </span>
          <button
            onClick={handleExitViewAs}
            className="underline font-medium hover:text-amber-100"
            type="button"
          >
            Voltar
          </button>
        </div>
      )}
    {/* relative z-[75]: os menus do header (sino, conta, empresas) ficam acima do
        conteúdo das páginas (FiltersBar z-[60], toolbar z-70) e abaixo dos modais (z-[80]+). */}
    <header className="relative z-[75] h-20 bg-surface border-b border-border-subtle px-6 w-full flex-shrink-0 flex items-center">
      <div className="flex items-center justify-between w-full">
        <div className="flex flex-1 items-center gap-4">
          {onBack && <IconBackButton onClick={onBack.onClick} label={onBack.label} />}
          <div>
            {eyebrow && <p className="text-xs text-muted">{eyebrow}</p>}
            <h1 className="text-2xl font-bold text-body">{title}</h1>
            {subtitle && (
              <p className="text-sm text-muted mt-1">{subtitle}</p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-4 flex-shrink-0">
          <GeminiStatusBanner />
          {actionButton && (
            <button
              onClick={actionButton.onClick}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
             type="button">
              <actionButton.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{actionButton.text}</span>
            </button>
          )}

          {/* Contadores - Apenas para Desenvolvedor */}
          {hasCap('can_use_dev_tools') && (
            <>
              {/* Contadores de Automações - Separados do perfil, à esquerda do nome */}
              {statusCounts !== null && (
                <button
                  onClick={() => navigate('/admin/automacoes/execucoes')}
                  className="flex items-center gap-2 text-xs border-r border-border-subtle pr-4 mr-4 hover:opacity-80 transition-opacity"
                  title="Ir para Execuções das Automações"
                 type="button">
                  {statusCounts.running > 0 && (
                    <div className="flex items-center gap-1.5 text-blue-600 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-400 px-2 py-1 rounded cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-950/50 transition-colors">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="font-semibold">{statusCounts.running}</span>
                    </div>
                  )}
                  {statusCounts.failed > 0 && (
                    <div className="flex items-center gap-1.5 text-red-600 bg-red-300 dark:bg-red-950/40 dark:text-red-300 px-2 py-1 rounded cursor-pointer hover:bg-red-100 dark:hover:bg-red-950/60 transition-colors animate-pulse">
                      <XCircle className="w-5 h-5" />
                      <span className="font-semibold">{statusCounts.failed}</span>
                    </div>
                  )}
                </button>
              )}
            </>
          )}

          {showCompanySelector && companyFilterApplies && (
            <div className="w-[260px]">
              <CompanyMultiSelect
                companies={availableCompanies}
                value={selectedCompanyIds}
                onChange={setSelectedCompanyIds}
              />
            </div>
          )}
          <AlertBell />
          <AccountMenu user={user} onLogout={logout} footerSlot={<ThemeToggle />} />
        </div>
      </div>
    </header>
    </>
  );
};

export default Header;
